import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, LabelList, XAxis } from 'recharts';
import type { TooltipProps } from 'recharts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { toast } from 'sonner';

import { type ExportFormat } from '@/components/atoms/ExportButton/ExportButton';
import { ExportButton } from '@/components/atoms/ExportButton/ExportButton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { type ChartConfig, ChartContainer, ChartTooltip } from '@/components/ui/chart';
import { cn } from '@/lib/utils';
import { buildExportFilename } from '@/modules/analytics/utils/exportFilename';
import { downloadCsv, downloadJson } from '@/util/fileManager';

import { type DateTimeSeriesPoint } from './hooks/useSingleAgentCustomInsights';
import { MetricTooltipCard, MetricTooltipRow } from '../../tooltip/MetricTooltipCard';

const CHART_ID = 'ai-agent-custom-datetime';
const MIN_POINT_WIDTH_PX = 48;
const SCROLL_THRESHOLD = 12;

type DateTimeLineInsightCardProps = {
  title: string;
  dateRangeLabel: string;
  series: DateTimeSeriesPoint[];
  className?: string;
};

const DateTimeBarTooltip = ({
  active,
  payload,
  label,
  metricLabel,
}: TooltipProps<ValueType, NameType> & { metricLabel: string }) => {
  if (!active || !payload?.length) {
    return null;
  }
  const value = payload[0]?.value as number | undefined;
  return (
    <MetricTooltipCard title={label as string}>
      <MetricTooltipRow label={metricLabel} value={value != null ? value.toLocaleString() : '–'} />
    </MetricTooltipCard>
  );
};

export const DateTimeLineInsightCard = ({
  title,
  dateRangeLabel,
  series,
  className,
}: DateTimeLineInsightCardProps) => {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);

  const chartConfig = useMemo<ChartConfig>(
    () => ({ value: { label: title, color: 'var(--chart-1)' } }),
    [title]
  );

  const isScrollable = series.length > SCROLL_THRESHOLD;

  const handleExport = useCallback(
    (exportFormat: ExportFormat) => {
      try {
        setIsExporting(true);
        const filename = buildExportFilename('datetime_insight');
        const rows = series.map(({ label, value }) => ({ date: label, value }));

        if (exportFormat === 'json') {
          downloadJson({ datetime_insight: rows }, filename);
        } else {
          downloadCsv(rows, filename);
        }
      } catch {
        toast.error(t('analytics.export_error'));
      } finally {
        setIsExporting(false);
      }
    },
    [series, t]
  );

  return (
    <Card
      className={cn(
        'flex h-full min-h-0 flex-col rounded-lg border-border bg-background text-foreground shadow-none',
        className
      )}
    >
      <CardHeader className="flex flex-row items-start justify-between gap-4 border-b border-border">
        <div className="flex min-w-0 flex-col gap-2">
          <CardTitle className="truncate text-base leading-none font-semibold">{title}</CardTitle>
          <CardDescription className="text-sm leading-5 text-muted-foreground">
            {dateRangeLabel}
          </CardDescription>
        </div>
        <div className="shrink-0">
          <ExportButton variant="icon" onExport={handleExport} isExporting={isExporting} />
        </div>
      </CardHeader>

      <CardContent className="flex flex-col items-stretch gap-2">
        <div className={cn('w-full', isScrollable && 'overflow-x-auto')}>
          <div
            className="flex flex-col gap-2"
            style={
              isScrollable ? { minWidth: `${series.length * MIN_POINT_WIDTH_PX}px` } : undefined
            }
          >
            <ChartContainer
              id={CHART_ID}
              config={chartConfig}
              className="aspect-auto h-48 w-full [&>div]:h-full"
            >
              <BarChart
                data={series}
                margin={{ top: 20, right: 0, left: 0, bottom: 0 }}
                barCategoryGap="20%"
                accessibilityLayer
              >
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="label"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  interval={0}
                  className="fill-muted-foreground text-xs"
                />
                <ChartTooltip content={<DateTimeBarTooltip metricLabel={t('analytics.users')} />} />
                <Bar
                  dataKey="value"
                  fill="var(--chart-1)"
                  radius={4}
                  maxBarSize={MIN_POINT_WIDTH_PX}
                >
                  <LabelList
                    dataKey="value"
                    position="top"
                    className="fill-foreground"
                    fontSize={12}
                    formatter={(value: number) => value.toLocaleString()}
                  />
                </Bar>
              </BarChart>
            </ChartContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
